import { Ability } from '@casl/ability';
import { useGetCountyOptionsWithState } from '@dispatch/shared/hooks';
import { Actions, Subjects } from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  AsyncSelect,
  FreeSoloSelect,
  RadioField,
  Section,
  SelectField,
} from '@dispatch/web/ui';
import { Grid, Stack } from '@mui/material';
import { useGeneralDataContext } from '@dispatch/shared/hooks';
import React, { useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useDebounce } from 'react-use';

export type ExclusionZonesInformationProps = {
  control: Control<FieldValues>;
  index: number;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  isReadOnly?: boolean;
};

export const ExclusionZonesInformation = ({
  control,
  index,
  permissions,
  errors,
  isReadOnly = false,
}: ExclusionZonesInformationProps) => {
  const { statesOptions } = useGeneralDataContext();

  const [countyInput, setCountyInput] = useState<string>('');
  const [countyDebouncedValue, setCountyDebouncedValue] = useState<string>('');

  useDebounce(
    () => {
      setCountyDebouncedValue(countyInput);
    },
    1000,
    [countyInput]
  );

  const filteredCounties = useGetCountyOptionsWithState(
    countyDebouncedValue,
    !!countyDebouncedValue
  );

  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const zoneType = useWatch({ control, name: `locations.${index}.zoneType` });
  const { setValue } = useFormContext();

  const clearData = () => {
    setValue(`locations.${index}.${zoneType}.counties`, []);
    setValue(`locations.${index}.${zoneType}.zipCodes`, []);
    setValue(`locations.${index}.${zoneType}.states`, []);

    if (zoneType === 'exclusionZonesInformation') {
      setValue(`locations.${index}.inclusionZonesInformation`, {
        states: [],
        counties: [],
        zipCodes: [],
      });
    } else if (zoneType === 'inclusionZonesInformation') {
      setValue(`locations.${index}.exclusionZonesInformation`, {
        states: [],
        counties: [],
        zipCodes: [],
      });
    }
  };

  return (
    <Grid size={12}>
      <Stack>
        <RadioField
          control={control}
          id={`locations.${index}.zoneType`}
          name={`locations.${index}.zoneType`}
          sx={{ display: 'flex', flexDirection: 'row' }}
          defaultValue={zoneType}
          onChange={() => {
            clearData();
          }}
          buttons={[
            {
              label: 'Inclusion Zones',
              value: 'inclusionZonesInformation',
            },
            {
              label: 'Exclusion Zones',
              value: 'exclusionZonesInformation',
            },
          ]}
          disabled={isReadOnly}
        />
      </Stack>
      <Section>
        <SelectField
          id={`states`}
          key={zoneType}
          name={`locations.${index}.${zoneType}.states`}
          label="States"
          control={control}
          readOnly={!hasUpsertPermissions}
          multiple
          errors={
            errors && errors[`${zoneType}`] ? errors[`${zoneType}`] : null
          }
          options={statesOptions}
          disabled={isReadOnly}
        />
        <AsyncSelect
          id={`counties`}
          name={`locations.${index}.${zoneType}.counties`}
          label="Counties"
          control={control}
          readOnly={!hasUpsertPermissions}
          multiple
          errors={
            errors && errors[`${zoneType}`] ? errors[`${zoneType}`] : null
          }
          options={
            filteredCounties?.data?.flatMap((x) => [
              { title: x.title, value: x.title },
            ]) || []
          }
          setInput={setCountyInput}
          disabled={isReadOnly}
        />
        <FreeSoloSelect
          id={`zipCodes`}
          name={`locations.${index}.${zoneType}.zipCodes`}
          label="Zip Codes"
          control={control}
          readOnly={!hasUpsertPermissions}
          errors={
            errors && errors[`${zoneType}`] ? errors[`${zoneType}`] : null
          }
          pattern={/^[0-9]{5}((-[0-9]{4})|(-[0-9]{2}))?$/}
          disabled={isReadOnly}
        />
      </Section>
    </Grid>
  );
};
